import {useState} from 'react'
import {submitFeedback, downloadExcel} from '../api/client'
import DataView from './DataView'
import {Copy, ThumbsUp, ThumbsDown} from 'lucide-react'

function SqlBlock({sql}) {
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const handleCopy = async (e) => {
    e.stopPropagation()
    try {
      await navigator.clipboard.writeText(sql)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      const el = document.createElement('textarea')
      el.value = sql
      document.body.appendChild(el)
      el.select()
      document.execCommand('copy')
      document.body.removeChild(el)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <div className='sql-block'>
      <div className='sql-header'>
        <button className='sql-toggle' onClick={() => setOpen((o) => !o)}>
          <span>{open ? '▾' : '▸'}</span> SQL
        </button>
        <button className='sql-copy' onClick={handleCopy} title='Copy SQL'>
          {copied ? '✓ Copied' : <Copy size={16} />}
        </button>
      </div>
      {open && <pre className='sql-code'>{sql}</pre>}
    </div>
  )
}

function DownloadBtn({sessionId, totalRows}) {
  const [downloading, setDownloading] = useState(false)

  const handle = async () => {
    if (downloading) return
    setDownloading(true)
    try {
      await downloadExcel(sessionId)
    } catch (e) {
      console.error(e)
    } finally {
      setDownloading(false)
    }
  }

  return (
    <button className='download-btn' onClick={handle} disabled={downloading}>
      {downloading ? 'Preparing...' : `⬇ Download all ${totalRows} rows as Excel`}
    </button>
  )
}

function Feedback({msg, context}) {
  const [voted, setVoted] = useState(null)
  const [loading, setLoading] = useState(false)

  const handleVote = async (rating) => {
    if (voted || loading) return
    setLoading(true)
    try {
      await submitFeedback({
        sessionId: context?.activeSessionId || context?.sessionId || '',
        query: msg.userQuery || msg.query || '',
        sql: msg.sql || null,
        vdbScore: msg.vdb_score || null,
        rating,
        userId: context?.user?.user_id || null,
        orgId: context?.user?.org_id || null,
      })
      setVoted(rating)
    } catch {
      setVoted(rating)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className='feedback-row'>
      <button
        className={`feedback-btn ${voted === 'up' ? 'voted-up' : ''}`}
        onClick={() => handleVote('up')}
        disabled={!!voted || loading}
        title='Good response'
      >
        {' '}
        <ThumbsUp size={16} />{' '}
      </button>
      <button
        className={`feedback-btn ${voted === 'down' ? 'voted-down' : ''}`}
        onClick={() => handleVote('down')}
        disabled={!!voted || loading}
        title='Bad response'
      >
        {' '}
        <ThumbsDown size={16} />{' '}
      </button>
      {voted && (
        <span className='feedback-thanks'>{voted === 'up' ? 'Saved as reference' : 'Noted'}</span>
      )}
    </div>
  )
}

export default function Message({msg, context}) {
  const isUser = msg.role === 'user'
  const showFeedback = !isUser && !msg.loading && msg.content
  const showDownload = !isUser && msg.isLargeResult && msg.totalRows > 0

  // Split on blank lines so the backend's paragraph breaks (\n\n) render
  // as actual <p> elements instead of collapsing into one run-on block.
  const paragraphs =
    typeof msg.content === 'string' ? msg.content.split(/\n\s*\n/).filter((p) => p.trim()) : []

  return (
    <div className={`msg ${isUser ? 'msg-user' : 'msg-assistant'}`}>
      <div className='msg-role'>{isUser ? 'You' : 'MSR AI'}</div>
      <div className='msg-content'>
        {msg.loading && !msg.content ? (
          <span className='dots'>
            <span />
            <span />
            <span />
          </span>
        ) : paragraphs.length > 1 ? (
          paragraphs.map((p, i) => (
            <p key={i} className='msg-paragraph'>
              {p}
            </p>
          ))
        ) : (
          <>
            {msg.content}
            {msg.loading && <span className='cursor' />}
          </>
        )}

        {/* DataView — uses real SQL rows mapped by chart_config column names */}
        {msg.chartConfig && msg.rowsSample?.length > 0 && (
          <DataView chartConfig={msg.chartConfig} rowsSample={msg.rowsSample} />
        )}

        {/* {msg.sql && <SqlBlock sql={msg.sql} />} */}

        {showDownload && (
          <DownloadBtn
            sessionId={context?.activeSessionId || context?.sessionId}
            totalRows={msg.totalRows}
          />
        )}

        {showFeedback && <Feedback msg={msg} context={context} />}
      </div>
    </div>
  )
}
